// Settings: which model renders your tiles, and — behind the same gear —
// the sampler, scheduler and VAE that sogni-web exposes under Advanced.
//
// The model used to be a server-side environment variable, so trying Dark
// Beast meant editing .env and restarting — and leaving it set silently
// changed the model for everything afterwards, which is exactly how a local
// override went unnoticed. It is now an explicit, per-visitor choice that
// says what it costs.
//
// The advanced knobs follow the same rule: never a hard-coded menu. The
// allowed samplers, schedulers and VAEs differ per model, so they come from
// the platform's own model options and are stored per model — a sampler that
// is valid for Krea 2 Turbo may not exist for Dark Beast.

import { modelOptions } from './sogni.js';

const KEY = 'sogni-infinity-model';
const ADVANCED_KEY = 'sogni-infinity-advanced';
const FILTER_KEY = 'sogni-infinity-content-filter';
const FORMAT_KEY = 'sogni-infinity-format';
const KREA_BYPASS_LORA = 'krea2-filter-bypass-3';
const KREA_BYPASS_MODELS = new Set([
  'krea2_turbo_fp8_scaled',
  'krea2_identity_edit_v1_2',
  'dark_beast_krea2_fp8',
  'dark_beast_krea2_identity_edit_v1_2'
]);
const KREA_BYPASS_DEFAULT = 1;
const KREA_BYPASS_MIN = 0.5;
const KREA_BYPASS_MAX = 100;

// Human labels for the raw option ids, matching sogni-web's wording so the
// same choice reads the same in both apps. Anything unlisted falls back to a
// prettified id rather than being hidden.
const SAMPLER_LABEL = {
  euler: 'Euler',
  euler_ancestral: 'Euler Ancestral',
  euler_ancestral_cfg_pp: 'Euler Ancestral CFG++',
  heun: 'Heun',
  dpmpp_2m: 'DPM++ 2M',
  dpmpp_2m_sde: 'DPM++ 2M SDE',
  dpmpp_sde: 'DPM++ SDE',
  dpmpp_3m_sde: 'DPM++ 3M SDE',
  dpmpp_2s_ancestral: 'DPM++ 2S Ancestral',
  ddim: 'DDIM',
  ddpm: 'DDPM',
  er_sde: 'ER SDE',
  res_multistep: 'Res Multistep',
  res_multistep_cfg_pp: 'Res Multistep CFG++',
  uni_pc: 'UniPC',
  lcm: 'LCM (Latent Consistency Model)',
  lms: 'LMS',
  dpm_2: 'DPM 2',
  dpm_2_ancestral: 'DPM 2 Ancestral',
  dpm_fast: 'DPM Fast',
  dpm_adaptive: 'DPM Adaptive'
};

const SCHEDULER_LABEL = {
  simple: 'Simple',
  normal: 'Normal',
  karras: 'Karras',
  exponential: 'Exponential',
  sgm_uniform: 'SGM Uniform',
  ddim_uniform: 'DDIM Uniform',
  beta: 'Beta',
  linear_quadratic: 'Linear Quadratic',
  kl_optimal: 'KL Optimal',
  linear: 'Linear',
  leading: 'Leading'
};

const VAE_LABEL = {
  'qwen_image_vae.safetensors': 'Qwen Image VAE',
  'krea2RealVae_v10.safetensors': 'Krea 2 Real VAE',
  'wan_2.1_vae.safetensors': 'WAN 2.1 VAE'
};

const VAE_NOTE = {
  'qwen_image_vae.safetensors': 'Softer Qwen-style decode: gentler texture, a more analog finish.',
  'krea2RealVae_v10.safetensors':
    'Realism-forward: sharper skin texture and high-frequency detail.',
  'wan_2.1_vae.safetensors': 'Balanced Krea 2 output: clean edges, stable colour, moderate detail.'
};

const modal = document.getElementById('settings-modal');
const openBtn = document.getElementById('settings-btn');
const closeBtn = document.getElementById('settings-close');
const select = document.getElementById('model-select');
const note = document.getElementById('model-note');
const advancedEl = document.getElementById('advanced');
const resetBtn = document.getElementById('advanced-reset');
const vaeNote = document.getElementById('vae-note');
const contentFilterCheck = document.getElementById('content-filter-check');
const formatSelect = document.getElementById('format-select');
const bypassField = document.getElementById('krea-bypass-field');
const bypassCheck = document.getElementById('krea-bypass-check');
const bypassStrengthField = document.getElementById('krea-bypass-strength-field');
const bypassStrength = document.getElementById('krea-bypass-strength');
const bypassStrengthValue = document.getElementById('krea-bypass-strength-value');

// One descriptor per advanced knob, so populate/read/reset stay one code path.
const KNOBS = [
  { name: 'sampler', labels: SAMPLER_LABEL },
  { name: 'scheduler', labels: SCHEDULER_LABEL },
  { name: 'vae', labels: VAE_LABEL }
];
for (const knob of KNOBS) {
  knob.field = document.getElementById(`${knob.name}-field`);
  knob.select = document.getElementById(`${knob.name}-select`);
}

let models = [];
let fallback = null;
// Per-model advanced picks: { [modelId]: { sampler, scheduler, vae } }.
let advanced = {};
// Allowed lists + defaults for the model currently shown, once fetched.
let options = null;

function prettify(id) {
  return String(id)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function labelFor(knob, value) {
  return knob.labels[value] || prettify(value);
}

function loadAdvanced() {
  try {
    const parsed = JSON.parse(localStorage.getItem(ADVANCED_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function saveAdvanced() {
  try {
    localStorage.setItem(ADVANCED_KEY, JSON.stringify(advanced));
  } catch {
    /* private mode: the picks just do not persist */
  }
}

function stored() {
  const id = localStorage.getItem(KEY);
  // A stored id that is no longer offered must not strand the visitor on a
  // model the server has dropped.
  return models.find((m) => m.id === id) || null;
}

function currentModel() {
  return stored() || models.find((x) => x.id === fallback?.model) || models[0] || null;
}

/**
 * The default for one knob on the current model: the model's own tuned value
 * where the platform allows it (the research was run against the configured
 * VAE), otherwise whatever the platform calls default.
 */
function defaultFor(name, model) {
  const opt = options?.[name];
  if (!opt?.allowed?.length) return undefined;
  const tuned = model?.[name];
  if (tuned && opt.allowed.includes(tuned)) return tuned;
  // Falling back to the first allowed value rather than undefined keeps the
  // select honest: a tier with no declared default would otherwise display
  // one option while the request carried none.
  return opt.default || opt.allowed[0];
}

/** The knob values to render with: the visitor's picks, else the defaults. */
function advancedFor(model) {
  const picks = advanced[model?.id] || {};
  const out = {};
  for (const { name } of KNOBS) {
    const opt = options?.[name];
    const picked = picks[name];
    // A stored pick the platform no longer allows falls back to the default
    // rather than being sent and rejected.
    out[name] = opt?.allowed?.includes(picked) ? picked : defaultFor(name, model);
  }
  out.kreaBypassEnabled = picks.kreaBypassEnabled === true;
  const strength = Number(picks.kreaBypassStrength);
  out.kreaBypassStrength = Number.isFinite(strength)
    ? Math.max(KREA_BYPASS_MIN, Math.min(KREA_BYPASS_MAX, strength))
    : KREA_BYPASS_DEFAULT;
  return out;
}

// The model or a knob changed what a render will cost; let interested UI (the
// per-spin cost estimate) recompute. Decoupled via an event so settings.js
// stays unaware of who is listening.
function announceChange() {
  window.dispatchEvent(new CustomEvent('sogni-settings-changed'));
}

/**
 * Whether mature content is allowed in prompts and renders — i.e. the
 * sensitive content filter is OFF. It ships off and stays off unless a visitor
 * turns it on here, so an unset preference means allowed.
 *
 * Leaving the filter off is a content-policy choice, not a change to the model.
 * It is only free of side effects because sogni-socket stopped attaching
 * krea2-filter-bypass-3 whenever the filter is disabled: at strength 1.0 that
 * LoRA altered generation enough to halve the seamless-tiling rate, so the
 * filter-off path used to quietly degrade every tile. It no longer does, so
 * the toggle is purely about what content is permitted.
 */
export function allowMature() {
  return localStorage.getItem(FILTER_KEY) !== '1';
}

/**
 * The image format tiles are requested in. PNG is lossless, so tile edges stay
 * clean for seamless wrapping; JPG trades a little edge quality for much smaller
 * files — faster rolls and more favourites in IndexedDB. It ships PNG, and since
 * png/jpg are valid for every model it is a single global choice rather than a
 * per-model knob. Anything but an explicit 'jpg' reads as PNG, so a stale or
 * garbage stored value can never send an invalid format.
 */
export function selectedFormat() {
  return localStorage.getItem(FORMAT_KEY) === 'jpg' ? 'jpg' : 'png';
}

/** The model and knobs to render with. */
export function selectedModel() {
  const m = currentModel();
  if (!m) return { model: fallback?.model, vae: fallback?.vae };
  const knobs = advancedFor(m);
  const useBypass = KREA_BYPASS_MODELS.has(m.id) && knobs.kreaBypassEnabled;
  return {
    model: m.id,
    // Before the options fetch lands, fall back to the model's configured
    // VAE so a fast first render is not silently decoded differently.
    vae: knobs.vae || m.vae,
    sampler: knobs.sampler,
    scheduler: knobs.scheduler,
    ...(useBypass ? { loras: [KREA_BYPASS_LORA], loraStrengths: [knobs.kreaBypassStrength] } : {})
  };
}

function showNote() {
  const m = models.find((x) => x.id === select.value);
  note.textContent = m?.note || '';
  // The note lives in an ⓘ tooltip; with nothing to say, hide the icon too.
  note.closest('.info-tip').classList.toggle('hidden', !note.textContent);
}

function isChanged(model) {
  const picks = advancedFor(model);
  return (
    KNOBS.some(({ name }) => picks[name] !== defaultFor(name, model)) || picks.kreaBypassEnabled
  );
}

/** Fill one knob's select, hiding it when there is nothing to choose between. */
function paintKnob(knob, model, value) {
  const allowed = options?.[knob.name]?.allowed || [];
  // One option is not a choice; sogni-web hides the VAE row the same way.
  knob.field.classList.toggle('hidden', allowed.length < 2);
  knob.select.innerHTML = '';
  for (const id of allowed) {
    const opt = document.createElement('option');
    opt.value = id;
    opt.textContent = labelFor(knob, id);
    knob.select.appendChild(opt);
  }
  if (value) knob.select.value = value;
}

function paintAdvanced() {
  const model = currentModel();
  const picks = advancedFor(model);
  for (const knob of KNOBS) paintKnob(knob, model, picks[knob.name]);
  // The description belongs to a choice; with the row hidden there is none.
  const vaeHidden = KNOBS.find((k) => k.name === 'vae').field.classList.contains('hidden');
  vaeNote.textContent = vaeHidden ? '' : VAE_NOTE[picks.vae] || '';
  vaeNote.closest('.info-tip').classList.toggle('hidden', !vaeNote.textContent);
  const supportsBypass = KREA_BYPASS_MODELS.has(model?.id);
  bypassField.classList.toggle('hidden', !supportsBypass);
  bypassCheck.checked = supportsBypass && picks.kreaBypassEnabled;
  bypassStrength.value = String(picks.kreaBypassStrength);
  bypassStrengthValue.value = String(picks.kreaBypassStrength);
  bypassStrengthField.classList.toggle('hidden', !bypassCheck.checked);
  resetBtn.disabled = !isChanged(model);
  const anyVisible = supportsBypass || KNOBS.some((k) => !k.field.classList.contains('hidden'));
  advancedEl.classList.toggle('hidden', !anyVisible);
}

/**
 * Load the current model's allowed options. Failure is not fatal: the
 * advanced block stays hidden and rendering falls back to the configured
 * defaults, which is exactly how the app behaved before these controls.
 */
async function loadOptions() {
  const model = currentModel();
  if (!model) return;
  options = null;
  paintAdvanced();
  try {
    const fetched = await modelOptions(model.id);
    // A model whose id was picked while a slower fetch was in flight must not
    // be painted with another model's option lists.
    if (currentModel()?.id !== model.id) return;
    options = fetched?.type === 'image' ? fetched : null;
  } catch (err) {
    console.warn('model options unavailable:', err?.message || err);
    options = null;
  }
  paintAdvanced();
}

export function initSettings(config) {
  fallback = config;
  models =
    Array.isArray(config?.models) && config.models.length
      ? config.models
      : [{ id: config.model, label: config.model, vae: config.vae, note: '' }];
  advanced = loadAdvanced();

  select.innerHTML = '';
  for (const m of models) {
    const opt = document.createElement('option');
    opt.value = m.id;
    opt.textContent = m.label || m.id;
    select.appendChild(opt);
  }
  select.value = selectedModel().model;
  showNote();
  loadOptions();

  select.onchange = () => {
    localStorage.setItem(KEY, select.value);
    showNote();
    // Allowed samplers/schedulers/VAEs are per model, so the whole advanced
    // block is re-fetched rather than carried across.
    loadOptions();
    announceChange();
  };

  for (const knob of KNOBS) {
    knob.select.onchange = () => {
      const model = currentModel();
      if (!model) return;
      advanced[model.id] = { ...advanced[model.id], [knob.name]: knob.select.value };
      saveAdvanced();
      paintAdvanced();
      announceChange();
    };
  }

  bypassCheck.onchange = () => {
    const model = currentModel();
    if (!model || !KREA_BYPASS_MODELS.has(model.id)) return;
    advanced[model.id] = {
      ...advanced[model.id],
      kreaBypassEnabled: bypassCheck.checked,
      ...(bypassCheck.checked ? { kreaBypassStrength: KREA_BYPASS_DEFAULT } : {})
    };
    saveAdvanced();
    paintAdvanced();
  };

  bypassStrength.oninput = () => {
    bypassStrengthValue.value = bypassStrength.value;
  };
  bypassStrength.onchange = () => {
    const model = currentModel();
    if (!model || !KREA_BYPASS_MODELS.has(model.id)) return;
    advanced[model.id] = {
      ...advanced[model.id],
      kreaBypassStrength: Math.max(
        KREA_BYPASS_MIN,
        Math.min(KREA_BYPASS_MAX, Number(bypassStrength.value) || KREA_BYPASS_DEFAULT)
      )
    };
    saveAdvanced();
    paintAdvanced();
  };

  resetBtn.onclick = () => {
    const model = currentModel();
    if (!model) return;
    delete advanced[model.id];
    saveAdvanced();
    paintAdvanced();
    announceChange();
  };

  // Checked means the filter is ON; unchecked (the default) means mature
  // content is allowed. No announceChange: the filter does not affect the
  // per-spin cost estimate.
  if (contentFilterCheck) {
    contentFilterCheck.checked = !allowMature();
    contentFilterCheck.onchange = () => {
      try {
        localStorage.setItem(FILTER_KEY, contentFilterCheck.checked ? '1' : '0');
      } catch {
        /* private mode: the choice just does not persist */
      }
    };
  }

  // Format is valid for every model, so it is a single global choice rather
  // than a per-model knob, and it does not change the per-spin cost — so no
  // announceChange, matching the content filter above.
  if (formatSelect) {
    formatSelect.value = selectedFormat();
    formatSelect.onchange = () => {
      try {
        localStorage.setItem(FORMAT_KEY, formatSelect.value);
      } catch {
        /* private mode: the choice just does not persist */
      }
    };
  }

  openBtn.onclick = () => modal.classList.remove('hidden');
  closeBtn.onclick = () => modal.classList.add('hidden');
  // Clicking the backdrop closes, matching the other modals.
  modal.onclick = (e) => {
    if (e.target === modal) modal.classList.add('hidden');
  };
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.classList.contains('hidden')) modal.classList.add('hidden');
  });
}
